/**
 * @file Uniform spatial hash over the XZ plane.
 *
 * Broad phase for collision: turns an O(n²) pair test into a lookup of the few
 * cells a box touches. Rebuilt from scratch every frame, which is cheaper than
 * incremental updates at the entity counts this game targets and keeps the
 * structure stateless between frames.
 */

export class SpatialHash {
  /**
   * @param {number} cellSize Edge length of a cell, in world units.
   */
  constructor(cellSize) {
    /** @type {number} */
    this.cellSize = cellSize;
    /** @type {number} @private */
    this._inverseCellSize = 1 / cellSize;
    /** @type {Map<number, any[]>} @private */
    this._cells = new Map();
    /** @type {any[][]} Recycled bucket arrays. @private */
    this._bucketPool = [];
    /** @type {Set<any>} Dedupe set reused across queries. @private */
    this._seen = new Set();
  }

  /**
   * Hashes integer cell coordinates into a single key.
   * Uses a large odd multiplier so negative coordinates do not collide.
   * @param {number} cx Cell X.
   * @param {number} cz Cell Z.
   * @returns {number}
   * @private
   */
  _key(cx, cz) {
    return cx * 73856093 ^ cz * 19349663;
  }

  /**
   * Empties the grid, recycling the bucket arrays.
   * @returns {void}
   */
  clear() {
    for (const bucket of this._cells.values()) {
      bucket.length = 0;
      this._bucketPool.push(bucket);
    }
    this._cells.clear();
  }

  /**
   * Inserts an item under every cell its box touches.
   * @param {any} item Payload, usually an entity.
   * @param {number} minX Box minimum X.
   * @param {number} minZ Box minimum Z.
   * @param {number} maxX Box maximum X.
   * @param {number} maxZ Box maximum Z.
   * @returns {void}
   */
  insert(item, minX, minZ, maxX, maxZ) {
    const x0 = Math.floor(minX * this._inverseCellSize);
    const z0 = Math.floor(minZ * this._inverseCellSize);
    const x1 = Math.floor(maxX * this._inverseCellSize);
    const z1 = Math.floor(maxZ * this._inverseCellSize);

    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) {
        const key = this._key(cx, cz);
        let bucket = this._cells.get(key);
        if (!bucket) {
          bucket = this._bucketPool.pop() ?? [];
          this._cells.set(key, bucket);
        }
        bucket.push(item);
      }
    }
  }

  /**
   * Collects every distinct item whose cells intersect the given box.
   * @param {number} minX Query minimum X.
   * @param {number} minZ Query minimum Z.
   * @param {number} maxX Query maximum X.
   * @param {number} maxZ Query maximum Z.
   * @param {any[]} out Array cleared and filled with the results.
   * @returns {any[]} The same `out` array.
   */
  query(minX, minZ, maxX, maxZ, out) {
    out.length = 0;
    this._seen.clear();

    const x0 = Math.floor(minX * this._inverseCellSize);
    const z0 = Math.floor(minZ * this._inverseCellSize);
    const x1 = Math.floor(maxX * this._inverseCellSize);
    const z1 = Math.floor(maxZ * this._inverseCellSize);

    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) {
        const bucket = this._cells.get(this._key(cx, cz));
        if (!bucket) continue;
        for (const item of bucket) {
          if (this._seen.has(item)) continue;
          this._seen.add(item);
          out.push(item);
        }
      }
    }
    return out;
  }
}
